import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import {useExportErrorList} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const ErrorExportApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(15);
  const [securityGroupID, setSecurityGroupID] = useState<number>(1);
  const [moduleType, setModuleType] = useState<string>('Export');
  const [exportResult, setExportResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const exportMutation = useExportErrorList(
    (data) => {
      setExportResult(
        `✅ Export started successfully! Message: ${data.message}`
      );
    },
    (error) => {
      setExportResult(`❌ Export failed: ${error.message}`);
    }
  );

  const handleExport = () => {
    exportMutation.mutate({
      productionId,
      SecurityGroupID: securityGroupID,
      ModuleType: moduleType,
    });
  };

  const handleReset = () => {
    setExportResult('');
  };

  const handleCopyJson = async () => {
    if (exportResult) {
      try {
        await navigator.clipboard.writeText(exportResult);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Error Export API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Export production errors. Starts the generation process and returns a
          success message.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Export Error List
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/errors/export
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Export production errors. This is a GET request with query
            parameters (productionId, SecurityGroupID, ModuleType). Returns a
            message indicating that generation has started successfully.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupID}
              onChange={(e) => setSecurityGroupID(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduleType}
              onChange={(e) => setModuleType(e.target.value)}
              size="small"
              placeholder="e.g., Export, Production"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending
                ? '⏳ Exporting...'
                : '🚀 Export Errors'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
            {exportResult && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? '✅ Copied!' : '📋 Copy Result'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {exportMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">⏳ Exporting errors... Please wait...</Alert>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {exportMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {exportMutation.error?.message ||
                  'An error occurred while exporting errors'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {exportMutation.isSuccess && exportMutation.data && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="success">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ✅ Success
              </Typography>
              <Typography className="typo-text body3_size">
                {exportMutation.data.message}
              </Typography>
            </Alert>

            {/* JSON Response */}
            <Box sx={{marginTop: 2}}>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(exportMutation.data, null, 2)}</pre>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Result Display */}
      {exportResult && !exportMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Export Result
            </Typography>
            <Alert severity={exportResult.includes('✅') ? 'success' : 'error'}>
              {exportResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ErrorExportApiTest;
